import { useCallback, useMemo, useState } from "react";

/**
 * Leads-table selection (CRM-1449). Two shapes:
 * - "ids": the classic explicit set, possibly spanning several pages.
 * - "filter": every lead matching the current filters, minus the ones the user
 *   un-ticked afterwards. The server resolves the actual ids at action time.
 */
export type LeadSelection =
  | { mode: "ids"; ids: ReadonlySet<number> }
  | { mode: "filter"; excludedIds: ReadonlySet<number> };

const EMPTY_SELECTION: LeadSelection = { mode: "ids", ids: new Set<number>() };

export const isLeadSelected = (selection: LeadSelection, id: number) =>
  selection.mode === "ids"
    ? selection.ids.has(id)
    : !selection.excludedIds.has(id);

export const isSelectionEmpty = (selection: LeadSelection) =>
  selection.mode === "ids" && selection.ids.size === 0;

// Selection is stored together with the scope it was made in. Reading it back under a
// different scope yields the empty selection, so a filter change resets it in the very
// same render — no effect, no one-frame flash of the old selection.
interface ScopedSelection {
  scopeKey: string;
  selection: LeadSelection;
}

interface ScopedTotal {
  scopeKey: string;
  total: number;
}

interface UseLeadSelectionReturn {
  selection: LeadSelection;
  /** Effective number of selected leads (filter mode: total minus exclusions). */
  count: number;
  /** Matched total for this scope, held steady while the next page loads. */
  totalCount: number;
  isSelected: (id: number) => boolean;
  toggleRow: (id: number) => void;
  togglePage: (ids: number[], checked: boolean) => void;
  selectAllMatching: () => void;
  clear: () => void;
}

export const useLeadSelection = ({
  scopeKey,
  totalCount
}: {
  /**
   * Serialized narrowing state (filters, search, assignee, starred, type, scope).
   * Any change clears the selection — for BOTH modes — since acting on leads the
   * user can no longer see is the same footgun in either. Page and sort changes
   * must not be part of it.
   */
  scopeKey: string;
  /** The list query's total_count; undefined while a page is loading. */
  totalCount: number | undefined;
}): UseLeadSelectionReturn => {
  const [stored, setStored] = useState<ScopedSelection>({
    scopeKey,
    selection: EMPTY_SELECTION
  });

  // The list query drops total_count to undefined while a page loads. Remember the last
  // value seen in this scope so the count, banner and toolbar don't blink on page
  // navigation. Storing information from previous renders, per the React docs.
  const [knownTotal, setKnownTotal] = useState<ScopedTotal>({
    scopeKey,
    total: totalCount ?? 0
  });
  if (
    totalCount !== undefined &&
    (knownTotal.scopeKey !== scopeKey || knownTotal.total !== totalCount)
  )
    setKnownTotal({ scopeKey, total: totalCount });
  const effectiveTotal =
    totalCount ?? (knownTotal.scopeKey === scopeKey ? knownTotal.total : 0);

  // Un-ticking every matching lead is the same as having nothing selected.
  const normalize = useCallback(
    (candidate: LeadSelection): LeadSelection =>
      candidate.mode === "filter" &&
      effectiveTotal > 0 &&
      candidate.excludedIds.size >= effectiveTotal
        ? EMPTY_SELECTION
        : candidate,
    [effectiveTotal]
  );

  const selection = normalize(
    stored.scopeKey === scopeKey ? stored.selection : EMPTY_SELECTION
  );

  const update = useCallback(
    (updater: (previous: LeadSelection) => LeadSelection) => {
      setStored((prev) => ({
        scopeKey,
        selection: updater(
          normalize(
            prev.scopeKey === scopeKey ? prev.selection : EMPTY_SELECTION
          )
        )
      }));
    },
    [scopeKey, normalize]
  );

  const toggleRow = useCallback(
    (id: number) =>
      update((prev) => {
        if (prev.mode === "ids") {
          const ids = new Set(prev.ids);
          if (ids.has(id)) ids.delete(id);
          else ids.add(id);
          return { mode: "ids", ids };
        }
        const excludedIds = new Set(prev.excludedIds);
        if (excludedIds.has(id)) excludedIds.delete(id);
        else excludedIds.add(id);
        return { mode: "filter", excludedIds };
      }),
    [update]
  );

  const togglePage = useCallback(
    (ids: number[], checked: boolean) =>
      update((prev) => {
        if (prev.mode === "ids") {
          const next = new Set(prev.ids);
          ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
          return { mode: "ids", ids: next };
        }
        const excludedIds = new Set(prev.excludedIds);
        ids.forEach((id) =>
          checked ? excludedIds.delete(id) : excludedIds.add(id)
        );
        return { mode: "filter", excludedIds };
      }),
    [update]
  );

  const selectAllMatching = useCallback(
    () => update(() => ({ mode: "filter", excludedIds: new Set<number>() })),
    [update]
  );

  const clear = useCallback(() => update(() => EMPTY_SELECTION), [update]);

  const isSelected = useCallback(
    (id: number) => isLeadSelected(selection, id),
    [selection]
  );

  const count =
    selection.mode === "ids"
      ? selection.ids.size
      : Math.max(0, effectiveTotal - selection.excludedIds.size);

  return useMemo(
    () => ({
      selection,
      count,
      totalCount: effectiveTotal,
      isSelected,
      toggleRow,
      togglePage,
      selectAllMatching,
      clear
    }),
    [
      selection,
      count,
      effectiveTotal,
      isSelected,
      toggleRow,
      togglePage,
      selectAllMatching,
      clear
    ]
  );
};
